import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  FormControl,
  InputLabel,
  TextField,
  Paper,
  MenuList,
  MenuItem,
  ClickAwayListener,
  Popper,
  Box,
  Typography,
  CircularProgress
} from '@mui/material';
import { ExpandMore } from '@mui/icons-material';

interface OptimizedSelectProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: Array<{ value: string; label: string }>;
  fullWidth?: boolean;
  required?: boolean;
  error?: boolean;
  disabled?: boolean;
  loading?: boolean;
  helperText?: string;
  placeholder?: string;
}

const OptimizedSelect: React.FC<OptimizedSelectProps> = ({
  label,
  value,
  onChange,
  options,
  fullWidth = false,
  required = false,
  error = false,
  disabled = false,
  loading = false,
  helperText,
  placeholder = "Sélectionner..."
}) => {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  // 🚀 PERFORMANCE: Memoized filtered options with debouncing
  const filteredOptions = useMemo(() => {
    if (!searchTerm.trim()) return options;
    
    const term = searchTerm.toLowerCase();
    return options.filter(option => 
      option.label.toLowerCase().includes(term) ||
      option.value.toLowerCase().includes(term)
    );
  }, [options, searchTerm]);

  // 🚀 PERFORMANCE: Only show first 50 items for virtualization
  const virtualizedOptions = useMemo(() => {
    return filteredOptions.slice(0, 50);
  }, [filteredOptions]);

  // Find selected option label
  const selectedLabel = useMemo(() => {
    const selected = options.find(opt => opt.value === value);
    return selected ? selected.label : '';
  }, [options, value]);

  const handleClick = useCallback((event: React.MouseEvent<HTMLElement>) => {
    if (disabled || loading) return;
    setAnchorEl(event.currentTarget);
    setOpen(true);
    setSearchTerm('');
  }, [disabled, loading]);

  const handleClose = useCallback(() => {
    setOpen(false);
    setAnchorEl(null);
    setSearchTerm('');
  }, []);

  const handleSelect = useCallback((optionValue: string) => {
    onChange(optionValue);
    handleClose();
  }, [onChange, handleClose]);

  const handleSearchChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  }, []);

  return (
    <FormControl fullWidth={fullWidth} error={error} disabled={disabled}>
      <TextField
        label={label}
        value={selectedLabel}
        onClick={handleClick}
        placeholder={placeholder}
        required={required}
        error={error}
        helperText={helperText}
        disabled={disabled}
        InputProps={{
          readOnly: true,
          endAdornment: loading ? (
            <CircularProgress size={20} />
          ) : (
            <ExpandMore 
              sx={{ 
                transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s ease'
              }} 
            />
          ),
        }}
        sx={{
          cursor: disabled ? 'default' : 'pointer',
          '& .MuiInputBase-input': {
            cursor: disabled ? 'default' : 'pointer',
          }
        }}
      />

      <Popper
        open={open}
        anchorEl={anchorEl}
        placement="bottom-start"
        style={{ zIndex: 1300, width: anchorEl?.clientWidth }}
      >
        <ClickAwayListener onClickAway={handleClose}>
          <Paper 
            elevation={4}
            sx={{ 
              maxHeight: 300, 
              overflow: 'hidden',
              border: '1px solid #e0e0e0'
            }}
          >
            {/* 🔍 SEARCH BOX for large lists */}
            {options.length > 10 && (
              <Box p={1} borderBottom="1px solid #e0e0e0">
                <TextField
                  size="small"
                  placeholder="Rechercher..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                  fullWidth
                  autoFocus
                  variant="outlined"
                />
              </Box>
            )}

            {/* 📋 OPTIONS LIST with virtualization */}
            <MenuList 
              sx={{ 
                maxHeight: options.length > 10 ? 250 : 300,
                overflow: 'auto',
                padding: 0
              }}
            >
              {virtualizedOptions.length === 0 ? (
                <MenuItem disabled>
                  <Typography variant="body2" color="textSecondary">
                    Aucun résultat trouvé
                  </Typography>
                </MenuItem>
              ) : (
                virtualizedOptions.map((option) => (
                  <MenuItem
                    key={option.value}
                    onClick={() => handleSelect(option.value)}
                    selected={option.value === value}
                    sx={{
                      py: 1,
                      '&:hover': {
                        backgroundColor: '#f5f5f5'
                      },
                      '&.Mui-selected': {
                        backgroundColor: '#e3f2fd',
                        '&:hover': {
                          backgroundColor: '#bbdefb'
                        }
                      }
                    }}
                  >
                    <Typography variant="body2" noWrap>
                      {option.label}
                    </Typography>
                  </MenuItem>
                ))
              )}
              
              {/* Show "more results" indicator */}
              {filteredOptions.length > 50 && (
                <MenuItem disabled>
                  <Typography variant="caption" color="textSecondary">
                    +{filteredOptions.length - 50} autres résultats...
                  </Typography>
                </MenuItem>
              )}
            </MenuList>
          </Paper>
        </ClickAwayListener>
      </Popper>
    </FormControl>
  );
};

export default OptimizedSelect; 